import { useTranslations } from 'next-intl';
import Image from 'next/image';

import { Breadcrumbs } from '@/components/Breadcrumbs';
import { MainSearch } from '@/components/MainSearch';
import { PostItemTime } from '@/components/PostItem/_components/PostItemTime';
import { Seo } from '@/components/Seo';
import { StaticContent } from '@/components/StaticContent';
import { ArticleStructuredData } from '@/components/StructuredData/ArticleStructuredData';
import { Hr } from '@/components/_atoms/Hr';
import { Typography } from '@/components/_atoms/Typography';
import { PostsCarousel } from '@/components/_molecules/PostsCarousel';
import { useBlogPost } from '@/logic/blog/useBlogPost';
import { schemas } from '@/network/client';
import { getImagesUrlFromContentResponse } from '@/utils/content.utils';
import { getBlogPostUrl } from '@/utils/router.utils';
import { cn } from '@/utils/styles.utils';

import { parseBlogPostHeaders } from './BlogPostScreen.utils';
import { BlogPostShortContent } from './BlogPostShortContent';

import styles from './BlogPostScreen.module.css';

interface Props {
  categorySlug: string;
  postSlug: string;
}

export const BlogPostScreen = (props: Props) => {
  const t = useTranslations();
  const { categorySlug, postSlug } = props;

  const { post, categoryItems } = useBlogPost(categorySlug, postSlug);
  if (!post) {
    return null;
  }
  const {
    meta_description,
    meta_title,
    image,
    content,
    title,
    created_at,
    updated_at,
    read_time,
    breadcrumbs,
    category_name,
  } = post;

  const path = getBlogPostUrl(categorySlug, postSlug);
  const images = [...(image ? [image] : []), ...getImagesUrlFromContentResponse(content)];

  const posts = categoryItems.map((item) => ({
    ...item,
    path: `/blog/${item.category_slug}/${item.slug}`,
  }));

  const headers = parseBlogPostHeaders(content.sections || []);
  return (
    <>
      <Seo title={meta_title || ''} description={meta_description || ''} />
      <ArticleStructuredData title={title} createdAt={created_at} updatedAt={updated_at} images={images} path={path} />
      <div className="mb-6 mt-4 laptop:mb-10">
        <Breadcrumbs data={breadcrumbs as unknown as schemas['Path'][]}></Breadcrumbs>
      </div>

      <div
        className={cn(styles.grid, {
          [styles.grid_noImage!]: !image,
        })}
      >
        <div className={styles.short}>
          <BlogPostShortContent headers={headers} />
        </div>
        <div className={styles.title}>
          <Typography variant="body_s" bold color="grey-semi" className="mb-2">
            {category_name}
          </Typography>
          <Typography variant="header_l" bold as="h1" className="mb-6 text-header-l laptop:mb-8 laptop:text-header-xl">
            {title}
          </Typography>
          <PostItemTime createdAt={created_at} readTime={read_time} />
        </div>

        {image ? (
          <div className={styles.image}>
            <div className="-mx-4 overflow-hidden laptop:mx-0 laptop:rounded-lg">
              <Image
                alt={title}
                src={image}
                width={1174}
                height={488}
                style={{
                  objectFit: 'cover',
                }}
              />
            </div>
          </div>
        ) : null}

        <div className={cn(styles.html, 'pb-8 laptop:pb-20')}>
          <StaticContent content={content} />
        </div>
      </div>

      <Hr />
      <div className="py-8 laptop:py-20">
        <MainSearch header={t('blogPost.searchHeader')} />
      </div>
      <Hr />
      {!!categoryItems.length && (
        <div className="py-8 laptop:py-20">
          <Typography
            id="blogPost.similarProductsHeader"
            variant="header_l"
            as="h2"
            bold
            className="mb-6 text-header-l laptop:mb-10 laptop:text-header-xl"
          />
          <PostsCarousel posts={posts} />
        </div>
      )}
    </>
  );
};
